/**
 * Utility functions for punching time management
 * 
 * This module provides centralized logic for determining which user roles
 * should have their login/logout times recorded in the HR Admin Punching Time Panel.
 * 
 * All admins and staff members are tracked for attendance monitoring.
 */

/**
 * Check if a role should have punching time recorded
 * @param {string} role - The user role to check
 * @returns {boolean} - True if the role should be tracked
 */
export const shouldRecordPunchingTime = (role) => {
  const rolesToTrack = [
    // Staff roles
    'staff',
    
    // Admin roles (general)
    'admin',
    
    // Super admin roles
    'superadmin',
    'super_admin',
    
    // Purchase admin roles
    'purchaseadmin',
    'purchase_admin',
    
    // HR admin roles
    'hr_admin',
    'hradmin',
    
    // Accounts admin roles
    'accounts_admin',
    'accountsadmin',
    
    // Marketing admin roles
    'marketing_admin',
    'marketingadmin',
    
    // Digital marketing admin roles
    'digital_marketing_admin',
    'digitalmarketing_admin',
    'digitalmarketingadmin'
  ];
  
  return rolesToTrack.includes(role?.toLowerCase().replace(/[ _]/g, '') || '');
};

/**
 * Get all roles that should be tracked for punching time
 * @returns {string[]} - Array of roles that should be tracked
 */
export const getTrackedRoles = () => {
  return [
    'staff',
    'admin',
    'superadmin',
    'super_admin',
    'purchaseadmin',
    'purchase_admin',
    'hr_admin',
    'hradmin',
    'accounts_admin',
    'accountsadmin',
    'marketing_admin',
    'marketingadmin',
    'digital_marketing_admin',
    'digitalmarketing_admin',
    'digitalmarketingadmin'
  ];
};

/**
 * Check if the current user should have punching time recorded
 * @returns {boolean} - True if current user should be tracked
 */
export const shouldCurrentUserBeTracked = () => {
  const role = localStorage.getItem('role');
  return shouldRecordPunchingTime(role);
};

/**
 * Test function to verify all expected roles are covered
 * This can be called from browser console for debugging
 * @returns {object} - Test results showing which roles are tracked
 */
export const testRoleCoverage = () => {
  const allPossibleRoles = [
    // Staff roles
    'staff',
    
    // Admin roles (general)
    'admin',
    
    // Super admin roles
    'superadmin',
    'super_admin',
    
    // Purchase admin roles
    'purchaseadmin',
    'purchase_admin',
    
    // HR admin roles
    'hr_admin',
    'hradmin',
    
    // Accounts admin roles
    'accounts_admin',
    'accountsadmin',
    
    // Marketing admin roles
    'marketing_admin',
    'marketingadmin',
    
    // Digital marketing admin roles
    'digital_marketing_admin',
    'digitalmarketing_admin',
    'digitalmarketingadmin',
    
    // Test some variations
    'STAFF',
    'Admin',
    'SUPER_ADMIN',
    'Purchase_Admin',
    'HR Admin',
    'Accounts Admin',
    'Marketing Admin',
    'Digital Marketing Admin'
  ];
  
  const results = {
    tracked: [],
    notTracked: [],
    total: allPossibleRoles.length
  };
  
  allPossibleRoles.forEach(role => {
    if (shouldRecordPunchingTime(role)) {
      results.tracked.push(role);
    } else {
      results.notTracked.push(role);
    }
  });
  
  console.log('🔍 Punching Time Role Coverage Test:');
  console.log(`✅ Tracked Roles (${results.tracked.length}):`, results.tracked);
  console.log(`❌ Not Tracked Roles (${results.notTracked.length}):`, results.notTracked);
  console.log(`📊 Coverage: ${results.tracked.length}/${results.total} (${Math.round((results.tracked.length/results.total)*100)}%)`);
  
  return results;
};

// Make test function available globally for debugging
if (typeof window !== 'undefined') {
  window.testPunchingTimeRoles = testRoleCoverage;
}
